<template>
    <div class="order-received-popover-content">
        <OrderReceivedHeader
            :isAnyFulfillmentReady="isAnyFulfillmentReady"
            :animateIcon="animateIcon"
            :dailyOrderIncrement="confirmation.dailyOrderIncrement"
            :orderNumber="confirmation.orderNumber"
        />
        <PaymentSummary :confirmation="confirmation" />
        <PreorderPickupTimeSummary :orderPickUpDateTime="orderPickUpDateTime" :useCase="orderApplication" />
        <DeliveryAddressSummary :deliveryAddress="deliveryAddress" :orderApplication="orderApplication" />
        <LocationSummary :useCase="orderApplication" :allocation="allocation" />
        <UseCaseSummary
            :messageToUserAfterOrderPlaced="messageToUserAfterOrderPlaced"
            :useCase="orderApplication"
            :tableName="confirmation.tableName"
            :isAnyFulfillment="isAnyFulfillment"
            :isAnyFulfillmentReady="isAnyFulfillmentReady"
        />

        <div class="fulfillment-list-container" ref="fulfillmentListContainer"></div>

        <template v-if="isWaiterOrAdmin">
            <template v-if="showReceiptActionsForOperator">
                <!-- For operator with receipt actions show "more"/"receipt and more" and "continue" -->
                <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.more }}
                </button>

                <button v-if="!confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.receiptAndMore }}
                </button>

                <button class="btn transparent" type="button" @click="onContinueClick">
                    {{ trans.general.continue }}
                </button>
            </template>

            <template v-if="!showReceiptActionsForOperator">
                <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                    {{ trans.orderReceivedPopover.more }}
                </button>

                <!-- For operator with no receipt actions - show only "continue" -->
                <button
                    class="btn"
                    type="button"
                    :class="{ transparent: confirmation.isPayLater }"
                    @click="onContinueClick"
                >
                    {{ trans.general.continue }}
                </button>
            </template>
        </template>

        <!-- the kiosk has no receipt screen behind the popover, only the start page, so the
             single button ends the session instead of opening it -->
        <template v-if="isEndUser && kiosk.isKioskMode">
            <!-- on a terminal a long tap opens the browser context menu, which the guest can never close -->
            <button class="btn" type="button" @click="onFinishClick" @contextmenu.prevent>
                {{ trans.done.finish }}
            </button>
        </template>

        <template v-if="isEndUser && !kiosk.isKioskMode">
            <button v-if="confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                {{ trans.general.continue }}
            </button>

            <button v-if="!confirmation.isPayLater" class="btn" type="button" @click="onMoreClick">
                {{ trans.orderReceivedPopover.receiptAndMore }}
            </button>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { RealtimeFulfillmentList } from "@/app/elements/RealtimeFulfillmentList";
    import { trans } from "@/app/localization/Trans";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { Helper } from "@/app/ui/Helper";
    import type { IDeliveryAddress, IOrderPaymentConfirmation } from "@/data/model/Customer";
    import type { IOrderApplication } from "@/data/model/OrderApplication";
    import type { ITableAllocation } from "@/data/model/Table";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import DeliveryAddressSummary from "@/app/components/payment/orderReceived/DeliveryAddressSummary.vue";
    import LocationSummary from "@/app/components/payment/orderReceived/LocationSummary.vue";
    import OrderReceivedHeader from "@/app/components/payment/orderReceived/OrderReceivedHeader.vue";
    import PaymentSummary from "@/app/components/payment/orderReceived/PaymentSummary.vue";
    import PreorderPickupTimeSummary from "@/app/components/payment/orderReceived/PreorderPickupTimeSummary.vue";
    import UseCaseSummary from "@/app/components/payment/orderReceived/UseCaseSummary.vue";

    const props = defineProps<{
        confirmation: IOrderPaymentConfirmation;
        isWaiterOrAdmin: boolean;
        allocation: ITableAllocation | undefined;
        orderUseCase: IOrderApplication | undefined;
        showReceiptActionsForOperator: boolean;
        onStartOver?: () => void;
        onReceiptAndMore?: () => void;
        onFinish?: () => void;
        hide?: () => void;
        updateBodyHeight?: () => void;
    }>();

    const kiosk = useKioskStore();

    // State
    const orderId = computed(() => props.confirmation.orderId!);
    const orderServiceId = computed(() => props.confirmation.orderServiceId);
    const isEndUser = computed(() => !props.isWaiterOrAdmin);
    const messageToUserAfterOrderPlaced = computed(() => {
        console.log(isEndUser.value);
        console.log(props.confirmation.messageToUserAfterOrderPlaced);
        return isEndUser.value ? props.confirmation.messageToUserAfterOrderPlaced : undefined;
    });
    const orderApplication = ref(props.orderUseCase);
    const orderPickUpDateTime = ref<Date | undefined>(props.confirmation.scheduledFulfillmentDateTime);
    const deliveryAddress = ref<IDeliveryAddress | undefined>(props.confirmation.deliveryAddress);
    const animateIcon = ref(false);

    // fulfillments
    const fulfillmentListContainer = ref<HTMLDivElement | null>(null);
    let fulfillments: RealtimeFulfillmentList;

    const isAnyFulfillment = ref(false);
    const isAnyFulfillmentReady = ref(false);

    function setupFulfillmentList() {
        const $fulfillmentsContainer = $(fulfillmentListContainer.value!);
        fulfillments = new RealtimeFulfillmentList(orderId.value).renderTo($fulfillmentsContainer);
        fulfillments.onListChanged = () => onFulfillmentListChanged();

        refreshFulfillmentListStateAndBodyHeight();
    }

    function refreshFulfillmentListStateAndBodyHeight() {
        isAnyFulfillment.value = fulfillments.hasAnyFulfillment();
        isAnyFulfillmentReady.value = fulfillments.hasAnyFulfillmentReady();

        if (props.updateBodyHeight) {
            props.updateBodyHeight();
        }
    }

    function onFulfillmentListChanged() {
        refreshFulfillmentListStateAndBodyHeight();

        if (!fulfillments.hasAnyFulfillment()) {
            setTimeout(() => {
                if (props.hide) {
                    props.hide();
                }
            }, 800);
        }
    }
    // end fulfillments

    function onMoreClick() {
        if (props.hide) {
            props.hide();
        }
        if (props.onReceiptAndMore) {
            props.onReceiptAndMore();
        }
    }

    function onFinishClick() {
        if (props.hide) {
            props.hide();
        }
        if (props.onFinish) {
            props.onFinish();
        }
    }

    function onContinueClick() {
        if (props.onStartOver) {
            props.onStartOver();
        }
        if (props.hide) {
            props.hide();
        }
    }

    // lifecycle
    onMounted(async () => {
        setupFulfillmentList();
        TastierClient.context.onFulfillmentsChanged.add(onFulfillmentsChanged);
        fulfillments.startListening();

        await refreshHeight();

        window.setTimeout(async () => {
            animateIcon.value = true;
            await refreshHeight();
        }, 400);
    });

    onBeforeUnmount(() => {
        TastierClient.context.onFulfillmentsChanged.remove(onFulfillmentsChanged);
        fulfillments?.stopListening();
    });

    function onFulfillmentsChanged() {
        try {
            const newOrderPickUpDateTime = TastierClient.context.getActiveOrderPickUpDateTime(orderId.value);
            orderPickUpDateTime.value = newOrderPickUpDateTime;
        } catch (ex) {
            logError(ex);
        }
    }

    async function forceRefreshUseCaseSection() {
        try {
            orderApplication.value = Helper.getUseCaseByOrderOrOfferServiceId(orderServiceId.value);
            isAnyFulfillment.value = fulfillments.hasAnyFulfillment();
            isAnyFulfillmentReady.value = fulfillments.hasAnyFulfillmentReady();

            await refreshHeight();
        } catch (ex) {
            logError(ex);
        }
    }

    // todo: obsolete, remove after confirm flex-height is working
    async function refreshHeight() {
        await nextTick();
        if (props.updateBodyHeight) {
            props.updateBodyHeight();
        }
    }

    defineExpose({
        refreshUseCaseSection: forceRefreshUseCaseSection,
    });

    watch(orderPickUpDateTime, refreshHeight);
    watch(isAnyFulfillment, refreshHeight);
    watch(isAnyFulfillmentReady, refreshHeight);
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // todo: obsolete, remove after confirm flex-height is working
    .popover.order-received-popover {
        > .head {
            height: 46px;
        }

        > .body {
            top: 46px;
        }
    }

    .order-received-popover-content {
        padding-bottom: 1px; // todo: obsolete, remove after confirm flex-height is working

        .fulfillmentlist {
            max-height: 184px;
            min-height: 54px;
            overflow: auto;
            margin: 0 -26px;
            padding: 0 26px 14px 26px;
        }

        > .btn {
            display: block;
            height: 46px;
            margin: 7px 0 18px 0;
            background-color: var(--colorPrimary);
            color: #fff;
            border-radius: 23px;
            width: 100%;
            font-size: 17px;

            &.transparent {
                background-color: transparent;
                color: black;

                @include mixins.hover-and-active() {
                    background-color: transparent;
                }
            }

            @include mixins.hover-and-active() {
                background-color: var(--colorPrimaryHover);
            }
        }
    }

    // kiosk: the content is doubled, the same factor as the kiosk pay button
    .kiosk-mode .order-received-popover-content {
        .order-received-header {
            .icon {
                height: 140px;
                width: 140px;
                border-radius: 70px;

                > .fal {
                    line-height: 140px;
                    font-size: 60px;
                }
            }

            .state {
                font-size: 28px;
                line-height: 44px;
                padding: 38px 0 2px 0;
            }

            .ordernumber {
                font-size: 84px;
            }
        }

        .charge {
            > .info {
                > .amount {
                    font-size: 28px;
                    line-height: 42px;
                }

                // the markup comes from PaymentMethodIcon, which uses the pm-* classes
                .payment-method-container.has-payment-methods {
                    margin-left: 16px;
                }

                .pm-icon {
                    min-width: 52px;
                    height: 36px;
                    margin: 4px 16px 4px 0;
                }

                .pm-digits {
                    font-size: 28px;
                    line-height: 42px;
                    margin-right: 16px;

                    > .pm-bullets {
                        font-size: 20px;
                        line-height: 42px;
                        letter-spacing: 2px;
                        padding-right: 4px;
                    }
                }
            }

            .hint {
                font-size: 24px;
            }
        }

        .pre-order .pick-up-time,
        .location-name {
            font-size: 28px;
        }

        .usecase {
            font-size: 28px;
            line-height: 44px;
            padding: 24px 0;
        }

        .fulfillmentlist {
            max-height: 368px;
            min-height: 108px;

            > .fulfillment {
                height: 92px;

                > .item {
                    font-size: 32px;
                    line-height: 92px;

                    > .quantity {
                        width: 60px;
                    }
                }

                > .fulfillbtn {
                    height: 56px;
                    width: 96px;
                    border-radius: 48px;
                    margin: 18px 4px;
                }

                > .state {
                    padding: 8px 0 0 0;

                    > .time {
                        font-size: 56px;
                        line-height: 72px;
                        height: 72px;

                        > .fal {
                            font-size: 42px;
                            line-height: 72px;
                            padding-right: 20px;
                            padding-top: 2px;
                            padding-left: 10px;
                        }

                        > .unit {
                            font-size: 28px;
                            line-height: 36px;
                            padding-top: 28px;
                            padding-left: 8px;
                        }
                    }

                    &.fulfilled > .fa-check {
                        line-height: 76px;
                        font-size: 56px;
                    }
                }

                // sits next to the fulfill button, which is twice as wide now
                > .checkanimation {
                    right: 84px;
                    top: 8px;
                    height: 72px;
                    width: 72px;

                    > .fal {
                        line-height: 72px;
                        font-size: 40px;
                    }
                }
            }
        }

        // the action follows the kiosk pill, like the paybtn in the addtocard popover
        > .btn {
            height: 56px * variables.$kioskScale;
            border-radius: 28px * variables.$kioskScale;
            font-size: 22px * variables.$kioskScale;
            margin: 11px 0 27px 0;
        }
    }
</style>
